import { REQUIRED_INDICATOR_CLASS_NAME } from '../../../../components/baseV2';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { LIST_ROW_BG_COLORS, LIST_ROW_BG_SX, LIST_ROW_BORDER_SX } from '../../styles';

import type { SxProps, Theme } from '@mui/material';

// Extra pixels keep the 36px bordered icons from sitting on the row edge at zoom.
const AUTO_SAVE_ROW_BASE_SX = {
  overflow: 'visible',
  px: 2.5,
  py: 1.5,
  ...LIST_ROW_BORDER_SX,
};

const AUTO_SAVE_ROW_EDITING_SX = {
  outline: `1px solid ${FORTA_BLUE.light1}`,
  outlineOffset: -1,
};

// The caption straddles the input border: white edit fill above, the translucent row tint layered over white (opaque, hides the border) below.
const getRequiredCaptionRowSx = (index: number) => ({
  [`& .${REQUIRED_INDICATOR_CLASS_NAME}`]: {
    background: `linear-gradient(${FORTA_WHITE} calc(100% - 6px), transparent calc(100% - 6px)), linear-gradient(${LIST_ROW_BG_COLORS[index % 2]}, ${LIST_ROW_BG_COLORS[index % 2]}) ${FORTA_WHITE}`,
  },
});

// Alternating background per row, plus the outline while the row is being edited.
export const getAutoSaveRowSx = (index: number, isEditable: boolean): SxProps<Theme> => [
  AUTO_SAVE_ROW_BASE_SX,
  LIST_ROW_BG_SX[index % 2],
  getRequiredCaptionRowSx(index),
  isEditable ? AUTO_SAVE_ROW_EDITING_SX : {},
];

// Edit fills the inputs so they read as active; view stays transparent so the row shows through.
// !important beats FormFieldReadOnlyValue's gray fill and Ant's inline cutout style.
export const getAutoSaveFieldsSx = (isEditable: boolean) => {
  const backgroundColor = `${isEditable ? FORTA_WHITE : 'transparent'} !important`;
  return {
    '& .MuiOutlinedInput-root': { backgroundColor },
    '& .ant-picker': { backgroundColor },
  };
};

// Fill the active edit / expand button so the current state stands out.
export const AUTO_SAVE_ROW_ACTIVE_ICON_SX = {
  borderRadius: 2,
  backgroundColor: FORTA_BLUE.primary,
};

// Square hit-target (not MUI's default circle) plus visible overflow so the SVG
// stroke can paint at fractional zoom instead of being clipped by the button.
export const AUTO_SAVE_ROW_ICON_BUTTON_SX = {
  p: '2px',
  overflow: 'visible',
  flexShrink: 0,
  lineHeight: 0,
  borderRadius: 2,
  '& svg': { display: 'block', overflow: 'visible' },
};

// The expand chevron shares the active fill and rotates 180° while the row is open.
export const getAutoSaveExpandIconSx = (expanded: boolean): SxProps<Theme> => [
  AUTO_SAVE_ROW_ICON_BUTTON_SX,
  { transition: 'transform 150ms' },
  expanded ? AUTO_SAVE_ROW_ACTIVE_ICON_SX : {},
  expanded ? { transform: 'rotate(180deg)' } : {},
];
